import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { MxheroShareConfig } from "@dashboard-v3/api";

type AuthOptions = {
  organizationAuthenticationMethod: MxheroShareConfig["organizationAuthenticationMethod"];
  externalAuthenticationMethod: MxheroShareConfig["externalAuthenticationMethod"];
};

interface AuthOptionProps {
  options: AuthOptions;
  disabled: boolean;
  onChange: (update: Partial<MxheroShareConfig>) => void;
}

export default function AuthenticationOptions(props: AuthOptionProps) {
  const { t } = useTranslation("mxheroShares");
  const classes = useStyles();
  const { options } = props;

  function onMethodChange(e) {
    const { name, checked } = e.target;

    const update = { ...options };
    const [method] = name.split("_");
    const typeKey = name.includes("EXTERNAL")
      ? "externalAuthenticationMethod"
      : "organizationAuthenticationMethod";

    if (checked) {
      update[typeKey] = [...options[typeKey], method];
    } else {
      update[typeKey] = options[typeKey].filter(p => p !== method);
    }

    props.onChange(update);
  }

  return (
    <Box>
      <Typography variant="subtitle2">
        {t("settings.auth.title")}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.subtitle}
      >
        {t("settings.auth.subtitle")}
      </Typography>
      <div className={classes.optionContainer} style={{ marginBottom: 10 }}>
        <Typography variant="subtitle2" color="textSecondary">
          {t("settings.auth.option.external")}
        </Typography>
        {getAuthOptions("EXTERNAL", options).map(option => (
          <FormControlLabel
            key={`auth_option_external_${option.name}`}
            name={option.name}
            label={
              <Typography className={classes.optionLabel}>
                {t(option.label)}
              </Typography>
            }
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={option.checked}
                onChange={onMethodChange}
              />
            }
          />
        ))}
      </div>
      <div className={classes.optionContainer}>
        <Typography variant="subtitle2" color="textSecondary">
          {t("settings.auth.option.organization")}
        </Typography>
        {getAuthOptions("ORGANIZATION", options).map(option => (
          <FormControlLabel
            key={`auth_option_organization_${option.name}`}
            name={option.name}
            label={
              <Typography className={classes.optionLabel}>
                {t(option.label)}
              </Typography>
            }
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={option.checked}
                onChange={onMethodChange}
              />
            }
          />
        ))}
      </div>
    </Box>
  );
}

const useStyles = makeStyles({
  subtitle: { marginBottom: 20 },
  optionContainer: { marginBottom: 2 },
  optionLabel: { fontSize: 15 },
});

const getAuthOptions = (
  type: "EXTERNAL" | "ORGANIZATION",
  options: AuthOptions
) => {
  const methods = ["CODE", "GOOGLE", "MICROSOFT"] as const;
  const externalAuth = options.externalAuthenticationMethod;
  const organizationAuth = options.organizationAuthenticationMethod;

  return methods.map(method => {
    return {
      name: `${method}_${type}`,
      label: `settings.auth.method.${method}`,
      checked:
        type === "EXTERNAL"
          ? externalAuth.includes(method)
          : organizationAuth.includes(method),
    };
  });
};
